import { Matrix3, Matrix4, Vector3 } from 'three';
import { IHawkeyeRotMat } from '../interfaces/csv';
import { IHawkeyePitch } from '../interfaces/hawkeye/i-pitch';
import { ITrajectory, ITrajektRefBreak } from '../interfaces/pitches';
import {
  ISeamOrientation,
  ISpin,
  IVelocity,
} from '../interfaces/pitches/i-base';
import { BASEBALL_DRAG_COEFF, BallHelper } from './ball.helper';
import {
  DEG_TO_RAD,
  FTPS_TO_MPH,
  FT_TO_INCHES,
  MPH_TO_FTPS,
  RAD_RIGHT_ANGLE,
  getRotationMatrix,
  isNumber,
} from './math.utilities';
import { TrajHelper } from './trajectory.helper';

export class HawkeyeHelper {
  static getMatrix(data: Partial<IHawkeyeRotMat>): Matrix3 | undefined {
    try {
      const isValid = [
        data.Xx,
        data.Xy,
        data.Xz,
        data.Yx,
        data.Yy,
        data.Yz,
        data.Zx,
        data.Zy,
        data.Zz,
      ].every((m) => isNumber(m));

      if (!isValid) {
        throw Error('Hawkeye rotation matrix columns are invalid');
      }

      const safeData = data as IHawkeyeRotMat;

      return new Matrix3().set(
        // row 1
        safeData.Xx,
        safeData.Xy,
        safeData.Xz,
        // row 2
        safeData.Yx,
        safeData.Yy,
        safeData.Yz,
        // row 3
        safeData.Zx,
        safeData.Zy,
        safeData.Zz
      );
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  static getSeamsFromMatrix(mx: Matrix3, spinAxis: Vector3): ISeamOrientation {
    const trRotMx = getRotationMatrix({
      x_rad: RAD_RIGHT_ANGLE,
    });

    const trMx = mx.multiply(trRotMx);

    const invTrMx = trMx.invert();

    const point = spinAxis.applyMatrix3(invTrMx);
    return BallHelper.convertPointToSeams(point);
  }

  // todo: Confirm that Hawkeye rotation vector is equivalent to the heMatrix and not its inverse
  static getMatrixFromVector(heVector: Vector3): Matrix3 {
    const angle = heVector.length();
    const heMatrix = new Matrix3();

    if (angle < 1e-16) {
      heMatrix.identity();
    } else {
      const axis = heVector.clone().normalize();
      const heMatrix4 = new Matrix4().makeRotationAxis(axis, angle);
      heMatrix.setFromMatrix4(heMatrix4);
    }
    return heMatrix;
  }

  // todo: Confirm that spinAxis in getSeamsFromMatrix should be global (as it is for this function)
  static getSeamsFromVector(
    heVector: Vector3,
    spinAxis: Vector3
  ): ISeamOrientation {
    const heMatrix = this.getMatrixFromVector(heVector);
    return this.getSeamsFromMatrix(heMatrix, spinAxis);
  }

  static get3DSpeedMPH = (hePitch: IHawkeyePitch): IVelocity => {
    const speedMPH = hePitch.start.speed.mph;
    const phiRAD = hePitch.start.angle[0] * DEG_TO_RAD; // angle in xy plane relative to y-axis
    const thetaRAD = hePitch.start.angle[1] * DEG_TO_RAD; // angle in yz plane relative to y-axis

    const output: IVelocity = {
      vx: -speedMPH * Math.sin(phiRAD),
      vy: -speedMPH * Math.cos(phiRAD) * Math.cos(thetaRAD),
      vz: speedMPH * Math.sin(thetaRAD),
    };
    return output;
  };

  static getGlobal3DSpinRPM = (hePitch: IHawkeyePitch): ISpin | undefined => {
    if (!hePitch.spin.direction) {
      return undefined;
    }

    const global3DSpinRPM: ISpin = {
      wx: hePitch.spin.direction[0],
      wy: hePitch.spin.direction[1],
      wz: hePitch.spin.direction[2],
    };
    return global3DSpinRPM;
  };

  static getLocal3DSpinRPM = (hePitch: IHawkeyePitch): ISpin | undefined => {
    const global3DSpinRPM = this.getGlobal3DSpinRPM(hePitch);
    if (global3DSpinRPM === undefined) {
      return undefined;
    }

    const global3DVeloMPH = this.get3DSpeedMPH(hePitch);
    const output = BallHelper.getLocalSpin(global3DSpinRPM, global3DVeloMPH);
    return output;
  };

  static getBreaks = (hePitch: IHawkeyePitch): ITrajektRefBreak => {
    const output: ITrajektRefBreak = {
      xInches: -hePitch.deviation.horizontal * FT_TO_INCHES,
      zInches: hePitch.deviation.inducedVertical * FT_TO_INCHES,
    };
    return output;
  };

  static getExtrapolatedTraj = (
    hePitch: IHawkeyePitch,
    py: number
  ): ITrajectory => {
    // * get a nine parameter fit and extrapolate to known release py

    const startVelocityMPH = this.get3DSpeedMPH(hePitch);
    const ay =
      BASEBALL_DRAG_COEFF * Math.pow(startVelocityMPH.vy * MPH_TO_FTPS, 2);
    const tf =
      hePitch.duration === undefined
        ? TrajHelper.calc_tr({
            ay: ay,
            vy: startVelocityMPH.vy * MPH_TO_FTPS,
            py: hePitch.start.position[1],
            new_py: hePitch.end.position[1],
          })
        : hePitch.duration;

    const spin = this.getGlobal3DSpinRPM(hePitch);
    const wnet = spin ? BallHelper.getNetSpin(spin) : 0;

    const traj = TrajHelper.getTrajFromBallPathEndPoints({
      startPosition: {
        px: hePitch.start.position[0],
        py: hePitch.start.position[1],
        pz: hePitch.start.position[2],
      },
      endPosition: {
        px: hePitch.end.position[0],
        py: hePitch.end.position[1],
        pz: hePitch.end.position[2],
      },
      startVelocity: {
        vx: startVelocityMPH.vx * MPH_TO_FTPS,
        vy: startVelocityMPH.vy * MPH_TO_FTPS,
        vz: startVelocityMPH.vz * MPH_TO_FTPS,
      },
      timeDelta: tf,
      wnet: wnet,
    });

    const output = TrajHelper.extrapolateTrajectory(traj, py);

    return output;
  };

  static getExtrapolated3DSpeedMPH = (
    hePitch: IHawkeyePitch,
    py: number
  ): IVelocity => {
    // * get release velocity from extrapolated traj
    const extrapolatedTraj = this.getExtrapolatedTraj(hePitch, py);
    const output: IVelocity = {
      vx: extrapolatedTraj.vx * FTPS_TO_MPH,
      vy: extrapolatedTraj.vy * FTPS_TO_MPH,
      vz: extrapolatedTraj.vz * FTPS_TO_MPH,
    };

    return output;
  };

  static getExtrapolatedLocal3DSpinRPM = (
    hePitch: IHawkeyePitch,
    py: number
  ): ISpin | undefined => {
    // * get local spin with extrapolated velocity
    const global3DSpinRPM = this.getGlobal3DSpinRPM(hePitch);
    if (global3DSpinRPM === undefined) {
      return undefined;
    }

    const global3DVeloMPH = this.getExtrapolated3DSpeedMPH(hePitch, py);
    const output = BallHelper.getLocalSpin(global3DSpinRPM, global3DVeloMPH);
    return output;
  };

  static getExtrapolatedBreaks = (
    hePitch: IHawkeyePitch,
    py: number
  ): ITrajektRefBreak => {
    // * get breaks from extrapolated traj
    const extrapolatedTraj = this.getExtrapolatedTraj(hePitch, py);
    const output = TrajHelper.getBreaks(extrapolatedTraj);

    return output;
  };
}
